/**
 * useProductMiceData Hook
 * 
 * M.I.C.E hizmet çözümü verilerini parse eden ve transform eden custom hook
 * ProductMiceDataService'i kullanarak merkezi yapı
 */

import { useMemo } from 'react';
import {
    ProductMiceDataService,
    Service,
    Reference,
    OverviewStatItem,
    OverviewHighlight,
} from '@/services/ProductMiceDataService';
import type { ProcessTimelineStep } from '@/components/ui/ProcessTimeline';

export interface UseProductMiceDataReturn {
    serviceType: string | null;
    location: string[];
    serviceFeatures: {
        included: Service[];
    };
    references: Reference[];
    overview: string | null;
    overviewStats: OverviewStatItem[];
    overviewHighlight: OverviewHighlight;
    additionalInfo: string | null;
    /** Neden Biz — services_repeater kartları */
    whyUsItems: Service[];
    /** Süreç giriş metni (rich_text), timeline dışında */
    processDesc: string | null;
    /** Süreç adımları (services_repeater → timeline) */
    processSteps: ProcessTimelineStep[];
    ctaRequestHeadline: string | null;
    ctaRequestSubtext: string | null;
    tabFields: Array<{
        tabName: string;
        sectionId: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }>;
}

export function useProductMiceData(product: any, language: string = 'en'): UseProductMiceDataReturn {
    // Product objesi referansını stabilize et
    const productId = product?.id;
    const productMetas = product?.metas;
    const productFields = product?.fields;
    
    return useMemo(() => {
        const metas = productMetas || {};
        const fields = productFields || {};

        // Service type
        const serviceType = ProductMiceDataService.parseServiceType(metas);

        // Location (tagify)
        const location = ProductMiceDataService.parseLocation(metas);

        // Service features
        const serviceFeatures = ProductMiceDataService.parseServiceFeatures(metas);

        // References
        const references = ProductMiceDataService.parseReferences(metas);

        // Content fields
        const contentFields = ProductMiceDataService.parseContentFields(metas, fields, language);

        const overviewStats = ProductMiceDataService.parseOverviewStats(metas);
        const overviewHighlight = ProductMiceDataService.parseOverviewHighlight(metas);
        const whyUsItems = ProductMiceDataService.parseWhyUsItems(metas);

        const processDesc = ProductMiceDataService.parseProcessDesc(metas, fields);
        const rawSteps = ProductMiceDataService.parseProcessSteps(metas);
        const processSteps: ProcessTimelineStep[] = rawSteps.map((s) => ({
            title: s.title,
            description: s.description,
        }));

        // Tab fields
        const tabFields = ProductMiceDataService.parseTabFields(metas, fields, language);

        return {
            serviceType,
            location,
            serviceFeatures,
            references,
            overview: contentFields.overview,
            overviewStats,
            overviewHighlight,
            additionalInfo: contentFields.additionalInfo,
            whyUsItems,
            processDesc,
            processSteps,
            ctaRequestHeadline: metas?.cta_request_headline ? String(metas.cta_request_headline) : null,
            ctaRequestSubtext: metas?.cta_request_subtext ? String(metas.cta_request_subtext) : null,
            tabFields,
        };
    }, [productId, productMetas, productFields, language]);
}

